import { useEffect, useState } from "react";
import { api, DIMENSION_META } from "@/lib/api";
import ProgressRing from "@/components/ProgressRing";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from "recharts";
import { Users, BookOpen, GitBranch, AlertCircle, Download } from "lucide-react";
import { toast } from "sonner";

export default function SystemDashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard/system").then((r) => setD(r.data)).catch(() => toast.error("Leader access required")); }, []);

  if (!d) return <div className="p-16 text-center text-slate-500">Loading…</div>;

  const exportReport = () => {
    const w = window.open("", "_blank");
    if (!w) return toast.error("Popup blocked");
    const html = `<html><head><title>${d.org_name} — EQ Report</title>
      <style>body{font-family:Georgia,serif;padding:60px;color:#0B1E36;max-width:800px;margin:auto}
      h1{font-size:38px;margin-bottom:6px}h2{border-bottom:2px solid #EC4899;padding-bottom:8px;margin-top:40px}
      .stat{display:inline-block;margin-right:40px}.stat b{display:block;font-size:32px;color:#EC4899}
      .row{margin:10px 0;padding:10px 0;border-bottom:1px solid #eee}</style></head>
      <body><h1>${d.org_name}</h1><div>Future Counts Index™ Report · ${new Date().toLocaleDateString()}</div>
      <h2>Organizational Snapshot</h2>
      <div class="stat"><b>${d.maturity_index}</b>Maturity Index</div>
      <div class="stat"><b>${d.diagnostics_completed}/${d.member_count}</b>Baselines</div>
      <div class="stat"><b>${d.lesson_completions}</b>Lessons</div>
      <div class="stat"><b>${d.scenario_attempts}</b>Scenarios</div>
      <h2>Dimension Scores</h2>
      ${Object.entries(d.dimension_scores).map(([k, v]) => `<div class="row"><b>${DIMENSION_META[k].label}:</b> ${v}/100</div>`).join("")}
      <h2>Biggest Gap</h2><p>${d.biggest_gap ? DIMENSION_META[d.biggest_gap].label : "—"}</p>
      </body></html>`;
    w.document.write(html); w.document.close(); w.print();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10 fade-up">
      <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">{d.org_name}</div>
          <h1 className="font-serif text-4xl text-[#0B1E36]">System dashboard</h1>
        </div>
        <button onClick={exportReport} data-testid="export-report" className="inline-flex items-center gap-2 bg-[#0B1E36] text-white px-5 py-2.5 rounded-full font-semibold btn-lift hover:bg-[#132a4a]">
          <Download className="h-4 w-4" /> Export board report
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-6">
        <div className="bg-white p-8 rounded-2xl border border-slate-200 flex flex-col items-center">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Future Counts Index™</div>
          <ProgressRing value={d.maturity_index} size={190} color="#EC4899" label="of 100" testId="org-maturity" />
        </div>

        <div className="lg:col-span-2 grid grid-cols-2 gap-4">
          <StatCard icon={Users} label="Members" value={d.member_count} tint="#0B1E36" tid="stat-members" />
          <StatCard icon={Users} label="Baselines" value={`${d.diagnostics_completed}/${d.member_count}`} tint="#2C7A7B" tid="stat-baselines" />
          <StatCard icon={BookOpen} label="Lessons completed" value={d.lesson_completions} tint="#C9A961" tid="stat-lessons" />
          <StatCard icon={GitBranch} label="Scenarios run" value={d.scenario_attempts} tint="#EC4899" tid="stat-scenarios" />
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white p-8 rounded-2xl border border-slate-200">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Dimension scores</div>
          <div className="space-y-5">
            {Object.entries(DIMENSION_META).map(([k, m]) => (
              <div key={k} data-testid={`org-dim-${k}`}>
                <div className="flex items-baseline justify-between mb-2">
                  <div className="font-semibold text-[#0B1E36] text-sm">{m.label}</div>
                  <div className="font-serif text-xl text-[#0B1E36]">{d.dimension_scores[k]}</div>
                </div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full" style={{ width: `${d.dimension_scores[k]}%`, background: m.accent }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-slate-200">
          <div className="text-xs uppercase tracking-widest text-slate-500 mb-4">Maturity trend</div>
          {d.trend.length > 1 ? (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%" minHeight={220}>
                <LineChart data={d.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="month" tick={{ fill: "#64748B", fontSize: 12 }} />
                  <YAxis domain={[0, 100]} tick={{ fill: "#64748B", fontSize: 12 }} />
                  <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                  <Line type="monotone" dataKey="maturity" stroke="#EC4899" strokeWidth={3} dot={{ fill: "#EC4899", r: 5 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="text-sm text-slate-500 h-56 grid place-items-center">More data points needed — collect a second baseline.</div>
          )}
        </div>
      </div>

      {d.biggest_gap && (
        <div className="bg-[#EC4899]/8 border border-[#EC4899]/30 rounded-2xl p-8 mb-6 flex items-start gap-4" data-testid="insight-card">
          <AlertCircle className="h-6 w-6 text-[#EC4899] shrink-0 mt-1" />
          <div>
            <div className="text-xs uppercase tracking-widest text-[#EC4899] mb-1">Actionable insight</div>
            <div className="font-serif text-2xl text-[#0B1E36] leading-snug">
              Your leadership team's biggest gap this quarter is <span className="text-[#EC4899]">{DIMENSION_META[d.biggest_gap].label}</span>.
            </div>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl leading-relaxed">
              Assign lessons in this dimension to leaders below 60. Run one scenario tied to this competency at your next admin meeting.
            </p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="px-8 py-5 border-b border-slate-200 flex items-center justify-between">
          <div className="font-serif text-lg text-[#0B1E36]">Team roster</div>
          <div className="text-xs text-slate-500">{d.member_count} members</div>
        </div>
        <div className="divide-y divide-slate-100">
          {d.members.map((m) => (
            <div key={m.id} className="px-8 py-4 flex items-center gap-4" data-testid={`member-${m.id}`}>
              <div className="h-9 w-9 rounded-full bg-[#0B1E36] text-white grid place-items-center font-serif text-sm shrink-0">
                {m.name[0]?.toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-[#0B1E36] text-sm truncate">{m.name}</div>
                <div className="text-xs text-slate-500 capitalize">{m.role.replace("_", " ")}</div>
              </div>
              <div className="text-xs text-slate-500 hidden md:block">
                Streak: <span className="font-semibold text-[#EC4899]">{m.streak}</span>
              </div>
              <div className={`text-xs px-3 py-1 rounded-full font-semibold ${m.onboarded ? "bg-[#2C7A7B]/15 text-[#2C7A7B]" : "bg-slate-100 text-slate-500"}`}>
                {m.onboarded ? "Active" : "Pending baseline"}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint, tid }) {
  return (
    <div className="bg-white p-6 rounded-2xl border border-slate-200" data-testid={tid}>
      <div className="h-9 w-9 rounded-lg grid place-items-center mb-3" style={{ background: tint + "15" }}>
        <Icon className="h-4 w-4" style={{ color: tint }} />
      </div>
      <div className="font-serif text-3xl text-[#0B1E36]">{value}</div>
      <div className="text-xs text-slate-500 uppercase tracking-widest mt-1">{label}</div>
    </div>
  );
}
